// ProviderCardListingContent.tsx
import React from 'react';
import { Result } from '@coveo/headless';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import ProviderReviews from 'helpers/Reviews/ProviderReviews';

interface ContentProps {
  visibleResults: Result[];
  shouldShowToggle: boolean | number;
  showAll: boolean;
  showLessdataLable: string;
  showAlldataLable: string;
  totalResults: number;
  onToggleClick: () => void;
  providerProxyImageClass: string;
}

export const ProviderCardListingContent = ({
  visibleResults,
  shouldShowToggle,
  showAll,
  showLessdataLable,
  showAlldataLable,
  totalResults,
  onToggleClick,
  providerProxyImageClass,
}: ContentProps) => {
  const reviewsAnchorId = 'accordion-a20f8b32-0f19-45fd-93ec-42b39a857405';

  return (
    <div className="mt-10">
      <div>
        <ul className="mt-4 space-y-4">
          {visibleResults.map((item: Result, index) => {
            const degrees = item?.raw?.cwdegreetitles
              ? (item?.raw?.cwdegreetitles as string).replaceAll(';', '/')
              : '';
            const acceptingNewPatients = item.raw.cwacceptingnewpatients as string | undefined;
            const isNotAcceptingNewPatients =
              acceptingNewPatients?.trim().toLowerCase() === 'not accepting new patients';
            return (
              <li
                key={index}
                className="py-[30px] flex flex-col lg:flex-row border-b-2 border-gray-50"
              >
                <div className="flex-grow">
                  <LinkWrapper field={{ href: item?.clickUri }}>
                    <h4 className="text-blue-base font-bold text-24 md:text-28 leading-31 hover:underline hover:text-darkblue-base active:text-darkblue-base underline-offset-[6px]">
                      {item.raw.cwfirstname as string} {item.raw.cwlastname as string}{' '}
                      {degrees && (
                        <span>
                          <span>{', '}</span>
                          <span>{degrees}</span>
                        </span>
                      )}
                    </h4>
                  </LinkWrapper>
                  {Array.isArray(item?.raw?.cwspecialties) && (
                    <span className="block my-5 font-normal text-18 text-gray-900">
                      <PlainTextWrapper
                        field={{
                          value: item.raw.cwspecialties
                            .map((spec: string) => spec.trim())
                            .join(', '),
                        }}
                      />
                    </span>
                  )}
                  <div className="block lg:hidden w-full md:min-w-[250px] md:w-[250px] mb-8">
                    {item.raw.cwphoto ? (
                      <ImageWrapper
                        className="w-[100px] min-h-[125px] md:w-[270px] md:h-[270px] rounded-[8px] object-cover"
                        field={{ value: { src: `${item.raw.cwphoto}` } }}
                      />
                    ) : (
                      <div className={providerProxyImageClass}>
                        <SvgIcon
                          icon="user"
                          className="!h-[60px] !w-[60px] [&>path]:fill-darkblue-base"
                          size="lg"
                          aria-label="Provider Profile Video"
                        />
                      </div>
                    )}
                  </div>
                  <ProviderReviews
                    variant="search-card"
                    profilePageUrlOrAnchorId={reviewsAnchorId}
                    coveoSearchData={item}
                    providerURL={item?.clickUri as string}
                  />
                  {acceptingNewPatients && (
                    <div
                      className={`${
                        isNotAcceptingNewPatients ? 'bg-utility-red-50' : 'bg-utility-green-50'
                      } text-gray-900 font-nunitoSans text-18 font-normal leading-[31px] md:inline-flex flex md:items-center items-start lg:mt-16 mt-4 px-8 py-3 rounded-[6px] mb-12`}
                    >
                      <SvgIcon
                        icon={isNotAcceptingNewPatients ? 'xcircle' : 'check-circle'}
                        className={`${
                          isNotAcceptingNewPatients
                            ? '[&>path]:fill-utility-red-700'
                            : '[&>path]:fill-utility-green-700'
                        } mr-4 !h-[22px] !w-[22px]`}
                        size="lg"
                        aria-label={acceptingNewPatients}
                      />
                      <span>{acceptingNewPatients}</span>
                    </div>
                  )}
                </div>
                <div className="flex gap-x-24">
                  <div className="hidden lg:block max-w-[160px] w-full">
                    {item.raw.cwphoto ? (
                      <ImageWrapper
                        className="w-full min-h-[200px] rounded-[8px] object-cover"
                        field={{ value: { src: `${item.raw.cwphoto}` } }}
                      />
                    ) : (
                      <div className={providerProxyImageClass}>
                        <SvgIcon
                          icon="user"
                          className="!h-[80px] !w-[80px] [&>path]:fill-darkblue-base"
                          size="custom"
                          aria-label="Provider Profile Video"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
        {shouldShowToggle && (
          <ButtonWrapper
            aria-expanded={showAll}
            ctaVariant="tertiary"
            onClick={onToggleClick}
            className="px-7 py-4 font-centuryGothic text-18 leading-[25px] text-blue-base hover:bg-blue-50 hover:text-darkblue-base !rounded-[8px] focus:border-blue-base hover:no-underline bg-transparent flex items-center font-bold lg:mt-[40px] mt-[32px]"
          >
            {showAll
              ? showLessdataLable
              : showAlldataLable.replace('{num}', totalResults.toString())}
            <SvgIcon
              icon={showAll ? 'minus' : 'plus'}
              className="ml-4 w-[22px] h-[22px] mb-1 [&>path]:fill-current"
              size="custom"
              aria-label={showAll ? 'Collapse list' : 'Expand list'}
            />
          </ButtonWrapper>
        )}
      </div>
    </div>
  );
};
